/* eslint-disable no-param-reassign */
import { type Ref, type ShallowReactive, computed } from 'vue'

import {
  type FacetBackedCriterion,
  type FinderConfig,
  type FinderCriterion,
  isFacetBackedCriterion,
} from './useFinder.types'

export function findCriterion<T extends FinderCriterion['kind']>(
  config: FinderConfig,
  kind: T,
): Extract<FinderCriterion, { kind: T }> | undefined {
  return config.criteria.find(
    (criterion): criterion is Extract<FinderCriterion, { kind: T }> =>
      criterion.kind === kind,
  )
}

export function useFinder(
  config: FinderConfig,
  filtersInput: ShallowReactive<Record<string, string[]>>,
  priceInput: Ref<[number, number] | undefined>,
) {
  // Assigns a fresh array so shallowReactive reactivity fires even when values is empty.
  function setCriterionValues(
    criterion: FacetBackedCriterion,
    values: string[],
  ): void {
    filtersInput[criterion.facetName] = [...values]
  }

  function resetCriterion(criterion: FinderCriterion): void {
    if (criterion.kind === 'budget') {
      priceInput.value = undefined
    } else if (isFacetBackedCriterion(criterion)) {
      filtersInput[criterion.facetName] = []
    }
  }

  function reset(): void {
    for (const criterion of config.criteria) {
      resetCriterion(criterion)
    }
  }

  const isFinderActive = computed(() =>
    config.criteria.some((criterion) => {
      if (criterion.kind === 'budget') {
        return priceInput.value !== undefined
      }

      if (!isFacetBackedCriterion(criterion)) return false

      return (filtersInput[criterion.facetName]?.length ?? 0) > 0
    }),
  )

  const selectedOptionsCount = computed(() =>
    config.criteria.reduce((count, criterion) => {
      if (!isFacetBackedCriterion(criterion)) return count

      return count + (filtersInput[criterion.facetName]?.length ?? 0)
    }, 0),
  )

  return {
    isFinderActive,
    selectedOptionsCount,
    setCriterionValues,
    resetCriterion,
    reset,
  }
}
